/**
 * Wave 4 — Landing Pages v2 (list + detail) — talks to `/api/v1/lp-v2/*`.
 *
 * • List:   `/app/landing-pages`               →  `LandingPagesV2List`
 * • Detail: `/app/landing-pages/:id`           →  `LandingPageV2Detail`
 *
 * Detail view ships a block-level editor (headline/subheadline/CTA + section
 * text), a live HTML iframe preview (fetched via srcdoc so auth headers
 * apply), and three accordions:
 *
 *   • SEO / Schema.org  — one-click JSON-LD generation
 *   • CRO Audit         — advisory recommendations + score
 *   • A/B Tests         — create → start → stop → declare winner
 *
 * Legacy `LandingPages.jsx` is preserved untouched so pre-Wave-4 Jest suites
 * continue to pass; those components are no longer routed but remain
 * importable for tests.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Layers,
  Play,
  RefreshCw,
  Save,
  Search,
  Square,
  Sparkles,
  Trophy,
  Wand2,
} from "lucide-react";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const WRITE_ROLES = new Set(["owner", "admin", "manager"]);

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const d = Math.floor((Date.now() - t) / 1000);
  if (d < 60) return `${d}s`;
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function StatusPill({ status }) {
  const tone =
    status === "published"
      ? "bg-emerald-100 text-emerald-900"
      : status === "archived"
      ? "bg-stone-100 text-stone-900"
      : "bg-sky-100 text-sky-900";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
      data-testid={`lpv2-status-${status || "draft"}`}
    >
      {status || "draft"}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LIST
// ═══════════════════════════════════════════════════════════════════════

export function LandingPagesV2List() {
  const t = useT();
  const nav = useNavigate();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (regionFilter) params.region = regionFilter;
      if (statusFilter) params.status = statusFilter;
      const [pages, tpls] = await Promise.all([
        api.get("/lp-v2", { params }),
        api.get("/lp-v2/templates"),
      ]);
      setItems(pages.data.items || []);
      setTemplates(tpls.data.items || []);
    } catch (e) {
      // Non-fatal — keep the shell rendered.
    } finally {
      setLoading(false);
    }
  }, [regionFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      [it.title, it.slug, it.region, it.topic_cluster]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle)),
    );
  }, [items, q]);

  const regions = useMemo(() => {
    const s = new Set();
    for (const it of items) if (it.region) s.add(it.region);
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="space-y-4" data-testid="lpv2-list-page">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="lpv2-list-title">
            {t("landing_pages_v2.title", "Landing Pages")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "landing_pages_v2.subtitle",
              "Regionale Landing-Pages, Block-Editor, Live-Preview und Schema.org.",
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="lpv2-open-programmatic-btn"
            onClick={() => nav("/app/landing-pages/programmatic")}
          >
            <Layers className="mr-1 size-4" />
            {t("landing_pages_v2.open_programmatic", "Programmatische SEO")}
          </Button>
          {canWrite && (
            <Button
              data-testid="lpv2-new-page-btn"
              onClick={() => setCreateOpen(true)}
            >
              {t("landing_pages_v2.new_page", "+ Neue Landing-Page")}
            </Button>
          )}
        </div>
      </header>

      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 flex-1 min-w-[240px]">
            <Search className="size-4 text-muted-foreground" />
            <Input
              data-testid="lpv2-search-input"
              placeholder={t("landing_pages_v2.search_ph", "Suche nach Titel, Slug, Region…")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border-0 focus-visible:ring-0"
            />
          </div>
          <Select
            value={regionFilter || "__ALL__"}
            onValueChange={(v) => setRegionFilter(v === "__ALL__" ? "" : v)}
          >
            <SelectTrigger data-testid="lpv2-filter-region" className="w-[180px]">
              <SelectValue placeholder={t("landing_pages_v2.filter_region", "Region")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__" data-testid="lpv2-filter-region-all">
                {t("landing_pages_v2.filter_all", "Alle Regionen")}
              </SelectItem>
              {regions.map((r) => (
                <SelectItem key={r} value={r} data-testid={`lpv2-filter-region-${r}`}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter || "__ALL__"}
            onValueChange={(v) => setStatusFilter(v === "__ALL__" ? "" : v)}
          >
            <SelectTrigger data-testid="lpv2-filter-status" className="w-[160px]">
              <SelectValue placeholder={t("landing_pages_v2.filter_status", "Status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">
                {t("landing_pages_v2.filter_all_status", "Alle Status")}
              </SelectItem>
              <SelectItem value="draft">draft</SelectItem>
              <SelectItem value="published">published</SelectItem>
              <SelectItem value="archived">archived</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="lpv2-refresh-btn"
            aria-label="refresh"
          >
            <RefreshCw className="size-4" />
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground" data-testid="lpv2-loading">
          {t("common.loading", "Lade…")}
        </p>
      ) : filtered.length === 0 ? (
        <Card data-testid="lpv2-empty">
          <CardContent className="pt-6 pb-6 text-sm text-muted-foreground">
            {t(
              "landing_pages_v2.empty",
              "Noch keine Landing-Pages. Erstellen Sie eine oder starten Sie einen Programmatic-SEO-Batch.",
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y" data-testid="lpv2-list">
              {filtered.map((lp) => (
                <li
                  key={lp.id}
                  className="px-4 py-3 flex items-center gap-3"
                  data-testid={`lpv2-row-${lp.id}`}
                >
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-medium truncate"
                        data-testid={`lpv2-row-title-${lp.id}`}
                      >
                        {lp.title || lp.slug || lp.id.slice(0, 8)}
                      </span>
                      <StatusPill status={lp.status} />
                      {lp.region && (
                        <Badge
                          variant="outline"
                          className="text-[11px] font-mono"
                          data-testid={`lpv2-row-region-${lp.id}`}
                        >
                          {lp.region}
                        </Badge>
                      )}
                      {lp.topic_cluster && (
                        <Badge variant="secondary" className="text-[11px]">
                          {lp.topic_cluster}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      /{lp.slug} · {t("landing_pages_v2.updated", "aktualisiert")}{" "}
                      {relativeTime(lp.updated_at)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => nav(`/app/landing-pages/${lp.id}`)}
                    data-testid={`lpv2-row-open-${lp.id}`}
                  >
                    {t("landing_pages_v2.open", "Öffnen")}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <NewPageDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        templates={templates}
        onCreated={(id) => {
          setCreateOpen(false);
          nav(`/app/landing-pages/${id}`);
        }}
      />
    </div>
  );
}

function NewPageDialog({ open, onOpenChange, templates, onCreated }) {
  const t = useT();
  const [templateId, setTemplateId] = useState("");
  const [title, setTitle] = useState("");
  const [region, setRegion] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && templates.length && !templateId) setTemplateId(templates[0].id);
  }, [open, templates, templateId]);

  async function submit() {
    if (!templateId) {
      toast.error(t("landing_pages_v2.dialog.tpl_required", "Template wählen."));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/lp-v2", {
        template_id: templateId,
        title: title || undefined,
        region: region || undefined,
        topic_cluster: topic || undefined,
      });
      onCreated(r.data.id);
    } catch (e) {
      toast.error(t("landing_pages_v2.dialog.create_failed", "Erstellen fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lpv2-new-page-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("landing_pages_v2.dialog.title", "Neue Landing-Page")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">
              {t("landing_pages_v2.dialog.template", "Template")}
            </label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="lpv2-dialog-template">
                <SelectValue placeholder="Template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tpl) => (
                  <SelectItem
                    key={tpl.id}
                    value={tpl.id}
                    data-testid={`lpv2-dialog-tpl-${tpl.slug}`}
                  >
                    {tpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">
                {t("landing_pages_v2.dialog.title_label", "Titel")}
              </label>
              <Input
                data-testid="lpv2-dialog-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("landing_pages_v2.dialog.region", "Region")}
              </label>
              <Input
                data-testid="lpv2-dialog-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("landing_pages_v2.dialog.topic", "Topic-Cluster (optional)")}
            </label>
            <Input
              data-testid="lpv2-dialog-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="lpv2-dialog-cancel"
          >
            {t("common.cancel", "Abbrechen")}
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="lpv2-dialog-submit"
          >
            {busy
              ? t("common.submitting", "Erstelle…")
              : t("landing_pages_v2.dialog.submit", "Erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DETAIL — block editor + live preview + CRO/Schema/A-B accordions
// ═══════════════════════════════════════════════════════════════════════

export function LandingPageV2Detail() {
  const t = useT();
  const nav = useNavigate();
  const { id } = useParams();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const [lp, setLp] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadLp = useCallback(async () => {
    try {
      const r = await api.get(`/lp-v2/${id}`);
      setLp(r.data);
      setDirty(false);
    } catch (e) {
      toast.error(t("landing_pages_v2.load_failed", "Landing-Page nicht gefunden."));
    }
  }, [id, t]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const r = await api.get(`/lp-v2/${id}/preview-html`, {
        responseType: "text",
        transformResponse: [(x) => x],
      });
      setPreviewHtml(String(r.data || ""));
    } catch (e) {
      setPreviewHtml(
        `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:1em;color:#666">Preview unavailable</body></html>`,
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLp();
  }, [loadLp]);
  useEffect(() => {
    if (lp) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lp?.updated_at]);

  function setHeroField(k, v) {
    setLp((p) => ({ ...p, hero: { ...(p.hero || {}), [k]: v } }));
    setDirty(true);
  }
  function setTitle(v) {
    setLp((p) => ({ ...p, title: v }));
    setDirty(true);
  }
  function setMeta(v) {
    setLp((p) => ({ ...p, meta_description: v }));
    setDirty(true);
  }
  function setSectionProp(sectionId, mutator) {
    setLp((p) => ({
      ...p,
      sections: (p.sections || []).map((b) =>
        b.id === sectionId ? { ...b, props: mutator({ ...(b.props || {}) }) } : b,
      ),
    }));
    setDirty(true);
  }

  async function save() {
    if (!lp) return;
    setSaving(true);
    try {
      const r = await api.patch(`/lp-v2/${id}`, {
        title: lp.title,
        meta_description: lp.meta_description,
        hero: lp.hero,
        sections: lp.sections,
      });
      setLp(r.data);
      setDirty(false);
      toast.success(t("landing_pages_v2.saved", "Gespeichert."));
    } catch (e) {
      toast.error(t("landing_pages_v2.save_failed", "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  }

  async function generateCopy() {
    if (!lp) return;
    setSaving(true);
    try {
      const r = await api.post(`/lp-v2/${id}/generate-copy`, {
        framework: "CONVERSION_FIRST",
        sections: ["hero", "benefits", "faq"],
      });
      setLp(r.data.landing_page);
      toast.success(
        r.data.source === "llm"
          ? t("landing_pages_v2.copy_ok", "Copy neu generiert.")
          : t("landing_pages_v2.copy_fallback", "Template-Fallback verwendet."),
      );
    } catch (e) {
      toast.error(t("landing_pages_v2.copy_failed", "Copy-Generierung fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!lp) return;
    try {
      const r = await api.post(`/lp-v2/${id}/publish`);
      setLp((p) => ({ ...p, status: r.data.status, published_at: r.data.published_at }));
      toast.success(t("landing_pages_v2.published", "Veröffentlicht."));
    } catch (e) {
      toast.error(t("landing_pages_v2.publish_failed", "Veröffentlichen fehlgeschlagen."));
    }
  }

  if (!lp) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="lpv2-detail-loading">
        {t("common.loading", "Lade…")}
      </div>
    );
  }

  const benefits = (
    (lp.sections || []).find((s) => s.kind === "benefits_grid")?.props?.items || []
  ).slice(0, 6);
  const faq = (
    (lp.sections || []).find((s) => s.kind === "faq")?.props?.items || []
  ).slice(0, 8);

  return (
    <div className="space-y-4" data-testid="lpv2-detail">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/app/landing-pages")}
            data-testid="lpv2-back-btn"
          >
            <ArrowLeft className="mr-1 size-4" />
            {t("landing_pages_v2.back", "Zurück")}
          </Button>
          <h1
            className="text-2xl font-semibold mt-1"
            data-testid="lpv2-detail-title"
          >
            {lp.title || lp.slug}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusPill status={lp.status} />
            {lp.region && (
              <Badge variant="outline" className="font-mono text-[11px]">
                {lp.region}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground font-mono">/{lp.slug}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <>
              <Button
                variant="outline"
                onClick={generateCopy}
                data-testid="lpv2-generate-copy-btn"
                disabled={saving}
              >
                <Wand2 className="mr-1 size-4" />
                {t("landing_pages_v2.generate_copy", "Copy generieren")}
              </Button>
              <Button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="lpv2-save-btn"
              >
                <Save className="mr-1 size-4" />
                {saving
                  ? t("common.saving", "Speichere…")
                  : t("landing_pages_v2.save", "Speichern")}
              </Button>
              {lp.status !== "published" && (
                <Button
                  variant="secondary"
                  onClick={publish}
                  data-testid="lpv2-publish-btn"
                >
                  {t("landing_pages_v2.publish", "Veröffentlichen")}
                </Button>
              )}
            </>
          )}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ─── Block Editor ────────────────────────────────────── */}
        <Card data-testid="lpv2-editor-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("landing_pages_v2.editor", "Block-Editor")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">
                {t("landing_pages_v2.field.title", "Titel")}
              </label>
              <Input
                data-testid="lpv2-field-title"
                value={lp.title || ""}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!canWrite}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {t("landing_pages_v2.field.meta", "Meta-Description")}
              </label>
              <Textarea
                data-testid="lpv2-field-meta"
                rows={2}
                value={lp.meta_description || ""}
                onChange={(e) => setMeta(e.target.value)}
                disabled={!canWrite}
              />
            </div>

            <div className="rounded-md border p-3 space-y-2" data-testid="lpv2-block-hero">
              <p className="text-xs font-medium uppercase text-muted-foreground">
                Hero
              </p>
              <Input
                data-testid="lpv2-hero-headline"
                placeholder="Headline"
                value={lp.hero?.headline || ""}
                onChange={(e) => setHeroField("headline", e.target.value)}
                disabled={!canWrite}
              />
              <Input
                data-testid="lpv2-hero-subheadline"
                placeholder="Subheadline"
                value={lp.hero?.subheadline || ""}
                onChange={(e) => setHeroField("subheadline", e.target.value)}
                disabled={!canWrite}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  data-testid="lpv2-hero-cta-text"
                  placeholder="Primary CTA"
                  value={lp.hero?.primary_cta_text || ""}
                  onChange={(e) => setHeroField("primary_cta_text", e.target.value)}
                  disabled={!canWrite}
                />
                <Input
                  data-testid="lpv2-hero-cta-href"
                  placeholder="CTA href"
                  value={lp.hero?.primary_cta_href || ""}
                  onChange={(e) => setHeroField("primary_cta_href", e.target.value)}
                  disabled={!canWrite}
                />
              </div>
            </div>

            {benefits.length > 0 && (
              <div className="rounded-md border p-3 space-y-2" data-testid="lpv2-block-benefits">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  Benefits
                </p>
                {benefits.map((b, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_2fr] gap-2"
                    data-testid={`lpv2-benefit-${i}`}
                  >
                    <Input
                      value={b.title || ""}
                      readOnly
                      className="text-xs"
                      data-testid={`lpv2-benefit-title-${i}`}
                    />
                    <Input
                      value={b.body || ""}
                      readOnly
                      className="text-xs"
                      data-testid={`lpv2-benefit-body-${i}`}
                    />
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "landing_pages_v2.benefits_readonly_hint",
                    "Regeneriere via 'Copy generieren' — künftig ist eine Inline-Bearbeitung geplant.",
                  )}
                </p>
              </div>
            )}

            {faq.length > 0 && (
              <div className="rounded-md border p-3 space-y-1" data-testid="lpv2-block-faq">
                <p className="text-xs font-medium uppercase text-muted-foreground">FAQ</p>
                <ul className="text-xs list-disc list-inside space-y-1">
                  {faq.map((f, i) => (
                    <li key={i} data-testid={`lpv2-faq-item-${i}`}>
                      <span className="font-medium">{f.q}</span>{" "}
                      <span className="text-muted-foreground">— {f.a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ─── Live Preview ────────────────────────────────────── */}
        <Card data-testid="lpv2-preview-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">
              {t("landing_pages_v2.preview", "Live-Vorschau")}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadPreview}
              data-testid="lpv2-refresh-preview-btn"
              disabled={previewLoading}
              aria-label="refresh preview"
            >
              <RefreshCw className="size-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <iframe
              data-testid="lpv2-preview-iframe"
              title="landing-page-preview"
              srcDoc={previewHtml}
              className="w-full h-[560px] rounded-md border bg-white"
              sandbox=""
            />
          </CardContent>
        </Card>
      </div>

      <Accordion type="multiple" defaultValue={["seo", "cro", "ab"]} className="space-y-2">
        <AccordionItem value="seo" data-testid="lpv2-acc-seo">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              <FileText className="size-4" />
              {t("landing_pages_v2.seo_schema", "SEO / Schema.org")}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <SeoSchemaPanel lp={lp} onUpdated={loadLp} canWrite={canWrite} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="cro" data-testid="lpv2-acc-cro">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              <Sparkles className="size-4" />
              {t("landing_pages_v2.cro_audit", "CRO-Audit")}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <CroPanel lp={lp} onUpdated={loadLp} canWrite={canWrite} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="ab" data-testid="lpv2-acc-ab">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              <Trophy className="size-4" />
              {t("landing_pages_v2.ab_tests", "A/B-Tests")}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <AbTestPanel lpId={id} canWrite={canWrite} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

// ─── SEO / Schema.org ────────────────────────────────────────────────

function SeoSchemaPanel({ lp, onUpdated, canWrite }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  async function generate() {
    setBusy(true);
    try {
      await api.post(`/lp-v2/${lp.id}/schema-generate`);
      toast.success(t("landing_pages_v2.schema_ok", "Schema.org JSON-LD aktualisiert."));
      onUpdated();
    } catch (e) {
      toast.error(t("landing_pages_v2.schema_failed", "Schema-Generierung fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }
  const jsonld = lp.schema_org?.generated_jsonld_str || "";
  const types = lp.schema_org?.types || [];
  return (
    <div className="space-y-2" data-testid="lpv2-seo-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {t("landing_pages_v2.schema_types", "Schema-Typen:")}
        </span>
        {types.length ? (
          types.map((typ) => (
            <Badge key={typ} variant="outline" className="text-[11px]">
              {typ}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("landing_pages_v2.no_schema_yet", "Noch nichts generiert.")}
          </span>
        )}
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={generate}
            disabled={busy}
            data-testid="lpv2-generate-schema-btn"
          >
            {busy
              ? t("common.working", "Arbeite…")
              : t("landing_pages_v2.generate_schema", "JSON-LD generieren")}
          </Button>
        )}
      </div>
      <pre
        data-testid="lpv2-schema-jsonld"
        className="rounded-md border bg-muted p-2 text-xs whitespace-pre-wrap font-mono max-h-64 overflow-auto"
      >
        {jsonld || "{}"}
      </pre>
    </div>
  );
}

// ─── CRO ─────────────────────────────────────────────────────────────

function CroPanel({ lp, onUpdated, canWrite }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  async function audit() {
    setBusy(true);
    try {
      await api.post(`/lp-v2/${lp.id}/cro-audit`);
      onUpdated();
      toast.success(t("landing_pages_v2.cro_ok", "CRO-Audit aktualisiert."));
    } catch (e) {
      toast.error(t("landing_pages_v2.cro_failed", "CRO-Audit fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }
  const recs = lp.cro_recommendations || [];
  return (
    <div className="space-y-2" data-testid="lpv2-cro-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {t("landing_pages_v2.cro_score", "Score:")}
        </span>
        <Badge
          variant="secondary"
          className="font-mono"
          data-testid="lpv2-cro-score"
        >
          {lp.cro_score ?? "—"}
        </Badge>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={audit}
            disabled={busy}
            data-testid="lpv2-run-cro-btn"
          >
            {busy
              ? t("common.working", "Arbeite…")
              : t("landing_pages_v2.run_cro", "CRO-Audit starten")}
          </Button>
        )}
      </div>
      {recs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="lpv2-cro-empty">
          {t(
            "landing_pages_v2.cro_no_recs",
            "Noch kein Audit ausgeführt. Klicken Sie auf 'CRO-Audit starten'.",
          )}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="lpv2-cro-list">
          {recs.map((r, i) => (
            <li
              key={i}
              className="rounded-md border p-2 space-y-1"
              data-testid={`lpv2-cro-rec-${i}`}
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[11px]">
                  {r.section}
                </Badge>
                <Badge
                  className={`text-[10px] ${
                    r.severity === "high"
                      ? "bg-rose-100 text-rose-900"
                      : r.severity === "medium"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-stone-100 text-stone-900"
                  }`}
                >
                  {r.severity}
                </Badge>
                {r.pattern && (
                  <span className="text-[11px] text-muted-foreground font-mono">
                    {r.pattern}
                  </span>
                )}
              </div>
              <p className="text-sm font-medium">{r.issue}</p>
              <p className="text-xs text-muted-foreground">{r.recommendation}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── A/B Tests ───────────────────────────────────────────────────────

function AbTestPanel({ lpId, canWrite }) {
  const t = useT();
  const [tests, setTests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [hypothesis, setHypothesis] = useState("");

  const load = useCallback(async () => {
    try {
      // No list-by-lp endpoint; we track tests via localStorage of last-created id.
      const ids = JSON.parse(localStorage.getItem(`lpv2.ab.${lpId}`) || "[]");
      const rows = [];
      for (const id of ids) {
        try {
          const r = await api.get(`/lp-v2/ab-tests/${id}`);
          rows.push(r.data);
        } catch (e) {
          /* deleted */
        }
      }
      setTests(rows);
    } catch (e) {
      /* noop */
    }
  }, [lpId]);
  useEffect(() => {
    load();
  }, [load]);

  async function create() {
    if (!name.trim()) {
      toast.error(t("landing_pages_v2.ab_name_required", "Name erforderlich."));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/lp-v2/${lpId}/ab-tests`, {
        name,
        hypothesis,
        variants: [
          { label: "Control", changes_json: {}, traffic_pct: 50 },
          { label: "Variant B", changes_json: {}, traffic_pct: 50 },
        ],
      });
      const ids = JSON.parse(localStorage.getItem(`lpv2.ab.${lpId}`) || "[]");
      ids.unshift(r.data.id);
      localStorage.setItem(`lpv2.ab.${lpId}`, JSON.stringify(ids.slice(0, 20)));
      setName("");
      setHypothesis("");
      load();
      toast.success(t("landing_pages_v2.ab_created", "A/B-Test angelegt."));
    } catch (e) {
      toast.error(t("landing_pages_v2.ab_create_failed", "Anlegen fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  async function transition(id, action, extraBody) {
    try {
      const url = extraBody
        ? `/lp-v2/ab-tests/${id}/${action}`
        : `/lp-v2/ab-tests/${id}/${action}`;
      await api.post(url, extraBody || {});
      load();
    } catch (e) {
      toast.error(t("landing_pages_v2.ab_transition_failed", "Transition fehlgeschlagen."));
    }
  }

  return (
    <div className="space-y-3" data-testid="lpv2-ab-panel">
      {canWrite && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground">
              {t("landing_pages_v2.ab_name", "Name")}
            </label>
            <Input
              data-testid="lpv2-ab-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("landing_pages_v2.ab_hypothesis", "Hypothese")}
            </label>
            <Input
              data-testid="lpv2-ab-hypothesis"
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
            />
          </div>
          <Button
            onClick={create}
            disabled={busy}
            data-testid="lpv2-ab-create-btn"
          >
            {t("landing_pages_v2.ab_create", "Anlegen")}
          </Button>
        </div>
      )}

      {tests.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="lpv2-ab-empty">
          {t("landing_pages_v2.ab_empty", "Noch keine A/B-Tests für diese Landing-Page.")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="lpv2-ab-list">
          {tests.map((tt) => (
            <li
              key={tt.id}
              className="rounded-md border p-2 space-y-1"
              data-testid={`lpv2-ab-row-${tt.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{tt.name}</span>
                <Badge variant="outline" className="text-[11px]">
                  {tt.status}
                </Badge>
                {tt.winner_variant_id && (
                  <Badge className="bg-emerald-100 text-emerald-900 text-[11px]">
                    <Trophy className="mr-1 size-3" />
                    {tt.winner_variant_id}
                  </Badge>
                )}
              </div>
              {tt.hypothesis && (
                <p className="text-xs text-muted-foreground">{tt.hypothesis}</p>
              )}
              {canWrite && (
                <div className="flex gap-2 flex-wrap pt-1">
                  {tt.status === "draft" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => transition(tt.id, "start")}
                      data-testid={`lpv2-ab-start-${tt.id}`}
                    >
                      <Play className="mr-1 size-3" />
                      Start
                    </Button>
                  )}
                  {tt.status === "running" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => transition(tt.id, "stop")}
                      data-testid={`lpv2-ab-stop-${tt.id}`}
                    >
                      <Square className="mr-1 size-3" />
                      Stop
                    </Button>
                  )}
                  {(tt.status === "running" || tt.status === "stopped") && (
                    <>
                      {(tt.variants || []).map((v) => (
                        <Button
                          key={v.id}
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            transition(tt.id, "declare-winner", { variant_id: v.id })
                          }
                          data-testid={`lpv2-ab-declare-${tt.id}-${v.id}`}
                        >
                          <Trophy className="mr-1 size-3" />
                          Winner: {v.label}
                        </Button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LandingPagesV2List;
